import {
  CaraWorkspaceArtifactType,
  CaraWorkspaceArtifactsFilterInput,
  EmailBulkSendListFieldsFragment,
  MailboxDraftFieldsFragment,
  MailboxDraftSyncState,
  MailboxDraftsFilterInput,
  OutboundEmailFieldsFragment,
  OutboundEmailStatus,
  OutboundEmailsFilterInput,
  OutboxArtifactFieldsFragment,
  OutboxFilterInput
} from '~/api/graphql';
import { mailboxDraftState } from './mailbox-drafts-columns';
import { outboundEmailProgress } from './outbound-emails-columns';

/**
 * An EMAIL artifact composed in Cara. Most of these also have a draft or a
 * send; the ones that do not are the emails that exist nowhere but Cara, which
 * is the only place the outbox can learn about them.
 */
export type OutboxArtifact = OutboxArtifactFieldsFragment;

/**
 * One email Cara composed, however many records it left behind. An email that
 * was written into a mailbox and then sent has a draft row and a send row
 * describing the same message, and this is that message: the sources are
 * fields, not rows.
 */
export interface OutboxRow {
  /** Stable across fetches, so a row keeps its React identity as sources merge in. */
  key: string;
  /**
   * The newest thing that happened to this email. The only ordering the outbox
   * has, and the one the merge watermark is measured in.
   */
  sortAt: Date;
  artifact?: OutboxArtifact;
  draft?: MailboxDraftFieldsFragment;
  /** The send this row's status reports; see buildOutboxRows. */
  outbound?: OutboundEmailFieldsFragment;
  /**
   * The other attempts, newest first, which is normally empty and is the whole
   * history when it is not.
   *
   * Kept rather than discarded because a failure is the part of the record
   * nobody may quietly lose: a retry writes a second send row, and reporting
   * only the newest would turn "sent on the third try" into "sent", erasing two
   * failures the agency may later have to account for.
   */
  earlierSends: OutboundEmailFieldsFragment[];
  /**
   * A bulk email, one row with none of the above for its whole life. Once it's sent, each of
   * its emails has a row of its own too.
   */
  bulkSend?: EmailBulkSendListFieldsFragment;
}

/**
 * How far an email has got. One value per state a reader has to tell apart,
 * ordered here from least to most travelled.
 *
 * `checking` sits in the vocabulary because it is what the status cell shows.
 *
 * `mailboxDisconnected` is the other value no ledger holds: the draft is still
 * parked as far as the ledger knows, and this says only that Cara can no longer
 * ask the mailbox about it. It is a state rather than a notice because
 * `inMailbox` — the row's ledger status — reads as "waiting for somebody to
 * send it", which is a claim about a mailbox nobody can currently see into.
 *
 * `goneUnproven` and `closed` are both "the mailbox let it go", and they are
 * separate because only one of them is a state the ledger can describe. A
 * draft that leaves the Drafts folder with no sent copy to match it is
 * retired as deleted, which is all an absence supports: a deletion, a send
 * whose Message-ID the provider never reported, and a sent copy since purged
 * are one value in the ledger and cannot be told apart here. `closed` is the
 * different unknown — a retirement reason written by a newer deploy than this
 * one — and reporting it as a deletion would invent a fact twice over.
 */
export type OutboxStatus =
  | 'checking'
  | 'mailboxDisconnected'
  | 'composed'
  | 'inMailbox'
  | 'scheduled'
  | 'sending'
  | 'interrupted'
  | 'sent'
  | 'sentFromMailClient'
  | 'failed'
  | 'cancelled'
  | 'replaced'
  | 'withdrawn'
  | 'goneUnproven'
  | 'closed';

/**
 * The furthest state the email has reached, read from the furthest source that
 * knows anything: a send outranks the draft it was sent from, and a draft
 * outranks the artifact it was written from.
 */
export const outboxRowStatus = (
  row: OutboxRow,
  opts: { nowMs: number; checking?: boolean; mailboxDisconnected?: boolean }
): OutboxStatus => {
  if (opts.checking) {
    return 'checking';
  }

  // Only over a row still resting on its draft. A send record is Cara's own
  // account of what it put on the wire and stays true whatever became of the
  // mailbox afterwards, so losing access to the mailbox is no reason to stop
  // reporting it.
  if (opts.mailboxDisconnected && row.draft && !row.outbound) {
    return 'mailboxDisconnected';
  }

  if (row.outbound) {
    const progress = outboundEmailProgress(row.outbound, opts.nowMs);
    // A send Cara reconstructed after the fact is still a send, but crediting
    // it to Cara would claim an action nobody took here. The send record's own
    // flag is what says so, rather than the retirement reason on the draft it
    // was reconstructed from: that draft is retired, and the ledger leaves
    // retired drafts out of a list unless they are asked for, so the row
    // usually has no draft to consult by the time this matters.
    if (progress === 'sent' && row.outbound.sentExternally) {
      return 'sentFromMailClient';
    }
    return progress;
  }

  if (row.draft) {
    switch (mailboxDraftState(row.draft)) {
      case 'live':
        return 'inMailbox';
      case 'sentViaCara':
        return 'sent';
      case 'sentExternally':
        return 'sentFromMailClient';
      case 'superseded':
        return 'replaced';
      case 'retracted':
        return 'withdrawn';
      case 'deletedExternally':
        return 'goneUnproven';
      default:
        return 'closed';
    }
  }

  return 'composed';
};

/**
 * How the copy in the mailbox has fallen behind the email Cara holds, in the
 * two ways a reader can act on: Cara wrote a newer version, or somebody edited
 * the words of the version the mailbox has. Either way the draft an agent would
 * press send on in their own client is out of date.
 *
 * A second axis, not a further status: an email's status says how far it has
 * travelled, and a draft that is sitting in the mailbox is exactly where the
 * status says it is whether or not the words are current.
 *
 * `UNKNOWN` is deliberately not reported here, though the composer treats it as
 * stale. Nothing was compared in that case — the draft names no artifact, or
 * predates the fingerprint — and a warning on a list has to mean the copy is
 * behind rather than that the question went unanswered.
 */
export type OutboxDraftStaleness = 'artifactVersionAhead' | 'contentChanged';

export const outboxRowStaleness = (row: OutboxRow): OutboxDraftStaleness | null => {
  if (!row.draft || mailboxDraftState(row.draft) !== 'live') {
    return null;
  }
  switch (row.draft.syncState) {
    case MailboxDraftSyncState.ArtifactVersionAhead:
      return 'artifactVersionAhead';
    case MailboxDraftSyncState.ContentChanged:
      return 'contentChanged';
    default:
      return null;
  }
};

/**
 * What identifies an email across the three ledgers: the EMAIL artifact id
 * every downstream record carries. A record naming no artifact stands alone
 * under its own id, since guessing would merge unrelated emails.
 */
const artifactKey = (artifactId: string | null | undefined, fallback: string): string =>
  artifactId?.trim() ? `artifact:${artifactId}` : fallback;

const later = (a: Date, b: Date | undefined): Date => (b && b > a ? b : a);

/**
 * A schedule cancelled before its delivery was claimed, as the server's status
 * filter skips it. It sent nothing and never speaks for the email, which reads
 * as the draft it went back to. Send now's Undo leaves no scheduled time, so it
 * still reads as Send cancelled.
 */
const isScheduleCancelled = (send: OutboundEmailFieldsFragment): boolean =>
  send.status === OutboundEmailStatus.Cancelled && !!send.scheduledFor;

/**
 * The send a row's subject, recipients, link and time are read from: the one
 * its status reports, or else, on a row with no draft or artifact loaded (the
 * artifact can be on a later page), the send it does have, such as a cancelled
 * schedule that doesn't speak for its status.
 */
export const outboxRowDisplaySend = (row: OutboxRow): OutboundEmailFieldsFragment | undefined =>
  row.outbound ?? (!row.draft && !row.artifact ? row.earlierSends[0] : undefined);

/**
 * Collapses the sources into one row per email, keyed by artifact.
 *
 * Where an artifact has more than one draft — one superseded by a later one —
 * the most recent wins, because the status the row reports is where the email
 * stands now and not where it once stood. Sends are kept whole instead: the
 * newest is the row's status and the rest are its history, except that a
 * cancelled schedule is only history.
 *
 * Sorted newest first, ties broken by key so the order is total: two rows
 * sharing a timestamp must not swap places between renders.
 */
export function buildOutboxRows(sources: {
  artifacts?: OutboxArtifact[];
  drafts?: MailboxDraftFieldsFragment[];
  outboundEmails?: OutboundEmailFieldsFragment[];
}): OutboxRow[] {
  const rows = new Map<string, OutboxRow>();

  const rowFor = (key: string, at: Date | string): OutboxRow => {
    const when = new Date(at);
    const existing = rows.get(key);
    if (existing) {
      existing.sortAt = later(existing.sortAt, when);
      return existing;
    }
    const created: OutboxRow = { key, sortAt: when, earlierSends: [] };
    rows.set(key, created);
    return created;
  };

  for (const artifact of sources.artifacts ?? []) {
    rowFor(`artifact:${artifact.id}`, artifact.composedAt).artifact = artifact;
  }

  for (const draft of sources.drafts ?? []) {
    const row = rowFor(
      artifactKey(draft.workspaceArtifactId, `draft:${draft.id}`),
      draft.createdAt
    );
    if (!row.draft || new Date(draft.createdAt) >= new Date(row.draft.createdAt)) {
      row.draft = draft;
    }
  }

  for (const email of sources.outboundEmails ?? []) {
    const row = rowFor(
      artifactKey(email.workspaceArtifactId, `outbound:${email.id}`),
      email.createdAt
    );
    row.earlierSends.push(email);
  }

  for (const row of rows.values()) {
    row.earlierSends.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const reportedIndex = row.earlierSends.findIndex((send) => !isScheduleCancelled(send));
    row.outbound = reportedIndex === -1 ? undefined : row.earlierSends.splice(reportedIndex, 1)[0];
  }

  return [...rows.values()].sort(compareOutboxRows);
}

/** buildOutboxRows' order, for rows added to its result. */
export const compareOutboxRows = (a: OutboxRow, b: OutboxRow): number =>
  b.sortAt.getTime() - a.sortAt.getTime() || a.key.localeCompare(b.key);

/**
 * Whether this email ended without going anywhere: the draft left the mailbox
 * and no send took its place. Not a failure and not a send — the four ways an
 * email stops being work in progress without becoming mail.
 *
 * Its own predicate rather than a status test at the call site because the page
 * hides these rows unless asked for them, and "closed" has to mean the same
 * thing to the switch that hides them as it does to the badge.
 */
export const outboxRowIsClosed = (row: OutboxRow, nowMs: number): boolean => {
  switch (outboxRowStatus(row, { nowMs })) {
    case 'replaced':
    case 'withdrawn':
    case 'goneUnproven':
    case 'closed':
      return true;
    default:
      return false;
  }
};

/**
 * Whether Cara asked the provider to send this email and never learned the
 * outcome, so the recipient may already have it.
 *
 * Read off the send records rather than the failure text: a reworded message
 * must not be able to turn a send that may be with the client into one that
 * plainly is not.
 *
 * Read across the whole history, because a second, tidier failure on top of an
 * unconfirmed one does not establish where the first one got to. An earlier
 * attempt still marked SENDING counts too: it has a newer attempt written on
 * top of it, so the sender that would have resolved it is gone.
 */
export const outboxRowSendUnconfirmed = (row: OutboxRow): boolean =>
  !!row.outbound?.sendUnconfirmed ||
  row.earlierSends.some(
    (send) => send.sendUnconfirmed || send.status === OutboundEmailStatus.Sending
  );

/**
 * Whether any attempt at this email reached the recipient.
 *
 * Nothing a later attempt does takes a delivered copy back, so this is the one
 * fact on a row that only ever accumulates: an email delivered on Tuesday and
 * refused on Wednesday is an email the client has.
 */
export const outboxRowDelivered = (row: OutboxRow): boolean =>
  [...(row.outbound ? [row.outbound] : []), ...row.earlierSends].some(
    (send) => send.status === OutboundEmailStatus.Sent
  );

/**
 * The ledgers whose read failed, so their absence from a row proves nothing.
 *
 * The artifact ledger is deliberately absent: it sits below both of these, so
 * failing to read it costs whole rows rather than making the rows that did
 * arrive say the wrong thing.
 */
export interface OutboxUnreadSources {
  drafts?: boolean;
  outbound?: boolean;
}

/**
 * Whether the status this row would report rests on a ledger nobody could read.
 *
 * A status is read from the furthest source that knows anything, so it is also
 * an assertion that every source beyond that one holds nothing: "Composed"
 * says no draft and no send exist, and "In drafts" says no send exists. A
 * failed read cannot support either claim, and the row has to say so rather
 * than report the stage its half of the record happens to end at.
 */
export const outboxRowStatusUnsound = (row: OutboxRow, unread: OutboxUnreadSources): boolean => {
  if (row.outbound) {
    return false;
  }
  if (unread.outbound) {
    return true;
  }
  return row.draft ? false : !!unread.drafts;
};

/**
 * One paginated source as the merge sees it: how far back it has been read and
 * whether there is anything left behind that.
 */
export interface OutboxSourceWindow {
  /** Oldest row fetched so far; null until the source's first page lands. */
  oldestFetchedAt: Date | null;
  exhausted: boolean;
  /**
   * Whether the source is exhausted because it broke rather than because it
   * ran out. Both mean nothing more is coming, and only one of them means the
   * rows already merged are all there were.
   */
  failed?: boolean;
}

/** Whether any source gave up part-way, so the merged list has holes in it. */
export const outboxSourcesIncomplete = (sources: OutboxSourceWindow[]): boolean =>
  sources.some((source) => source.failed);

/**
 * The oldest point the merged list can be trusted down to.
 *
 * Each source is paginated on its own cursor, so a row from one source is only
 * safe to render once every other source has been read at least as far back:
 * otherwise a still-unread row from a shallower source belongs above it, and
 * showing the deeper row first puts it on the wrong page for good. The binding
 * constraint is therefore the *newest* of the sources' oldest-read points.
 *
 * Infinite either way at the ends: a source whose first page has not arrived
 * blocks everything, and a set of sources with nothing left to read blocks
 * nothing.
 */
export function outboxWatermarkMs(sources: OutboxSourceWindow[]): number {
  let watermark = Number.NEGATIVE_INFINITY;
  for (const source of sources) {
    if (source.exhausted) {
      continue;
    }
    if (!source.oldestFetchedAt) {
      return Number.POSITIVE_INFINITY;
    }
    watermark = Math.max(watermark, source.oldestFetchedAt.getTime());
  }
  return watermark;
}

/**
 * The rows above the watermark, which are the ones whose position in the list
 * is final. Rows below it are held rather than dropped: reading further into
 * the binding source lowers the watermark and releases them, still in order.
 *
 * Strictly above, so that a source holding several rows at its oldest-read
 * timestamp cannot show some of them and hide the rest.
 */
export const visibleOutboxRows = (rows: OutboxRow[], watermarkMs: number): OutboxRow[] =>
  rows.filter((row) => row.sortAt.getTime() > watermarkMs);

/**
 * The drafts worth asking the provider about: the ones Cara believes are still
 * sitting in a Drafts folder. A retired draft has no copy left to check, an
 * artifact was never written to a mailbox, and a send in flight must not be
 * disturbed by a reconciliation that would retire the draft under it.
 *
 * A draft whose send failed or was interrupted stays eligible — that is
 * precisely the row where Cara's record and the mailbox are most likely to
 * disagree.
 */
export const reconcilableDraftIds = (rows: OutboxRow[], nowMs: number): string[] =>
  rows.flatMap((row) => {
    const draft = row.draft;
    if (!draft || draft.retiredAt) {
      return [];
    }
    if (row.outbound) {
      const progress = outboundEmailProgress(row.outbound, nowMs);
      if (progress === 'sending' || progress === 'sent') {
        return [];
      }
    }
    return [draft.id];
  });

/**
 * What reconciliation is allowed to change about a draft. Deliberately narrow:
 * everything else on the row is what Cara stamped when it wrote the draft, and
 * re-reading the mailbox establishes where the copy went, not what Cara did.
 *
 * `createdAt` is absent for the reason it matters — it is the row's sort value,
 * so a verdict landing mid-read cannot move a row under the reader's cursor.
 */
export type OutboxDraftPatch = Partial<
  Pick<MailboxDraftFieldsFragment, 'webLink' | 'sentViaCaraAt' | 'retiredAt' | 'retiredReason'>
>;

export const applyOutboxDraftPatches = (
  drafts: MailboxDraftFieldsFragment[],
  patches: ReadonlyMap<string, OutboxDraftPatch>
): MailboxDraftFieldsFragment[] =>
  patches.size === 0
    ? drafts
    : drafts.map((draft) => {
        const patch = patches.get(draft.id);
        return patch ? { ...draft, ...patch } : draft;
      });

/**
 * Mirrors overdueScheduledEmailRecoveryDelay plus one 5-minute recovery run: a
 * due email still unclaimed after this is stuck, and polling won't move it.
 */
const SCHEDULED_SEND_CLAIM_WAIT_MS = 10 * 60_000;

/** When a scheduled email is set to go out, or null for any other row. */
const waitingSendTimeMs = (row: OutboxRow): number | null => {
  const outbound = row.outbound;
  const isWaiting = outbound?.status === OutboundEmailStatus.Scheduled;
  if (!isWaiting || !outbound.scheduledFor) {
    return null;
  }
  return new Date(outbound.scheduledFor).getTime();
};

/** Whether a scheduled email's time has come, so its delivery is about to start. */
const isScheduledSendDue = (row: OutboxRow, nowMs: number): boolean => {
  const sendTimeMs = waitingSendTimeMs(row);
  if (sendTimeMs === null) {
    return false;
  }
  const overdueMs = nowMs - sendTimeMs;
  return overdueMs >= 0 && overdueMs <= SCHEDULED_SEND_CLAIM_WAIT_MS;
};

/**
 * The earliest send time still ahead among the scheduled emails on screen, so
 * the list can start watching the moment one comes due. Null when none is.
 */
export const nextScheduledSendTimeMs = (rows: OutboxRow[], nowMs: number): number | null => {
  const futureSendTimesMs = rows
    .map(waitingSendTimeMs)
    .filter((sendTimeMs): sendTimeMs is number => sendTimeMs !== null && sendTimeMs > nowMs);
  return futureSendTimesMs.length > 0 ? Math.min(...futureSendTimesMs) : null;
};

/**
 * Whether anything on screen is still expected to resolve on its own, which is
 * what the list polls for.
 */
export const hasUnresolvedOutboxRows = (rows: OutboxRow[], nowMs: number): boolean =>
  rows.some(
    (row) =>
      (row.outbound?.status === OutboundEmailStatus.Sending &&
        outboxRowStatus(row, { nowMs }) === 'sending') ||
      isScheduledSendDue(row, nowMs)
  );

/**
 * What the three sources are asked for.
 *
 * All three resolvers scope to the caller's agency and then apply the row's own
 * visibility triple, so asking with no narrowing is already "everything I may
 * read". There is nothing here that widens that, and nothing that could: the
 * reach of this list is the server's to decide.
 *
 * The reader's chips go to all three sources unchanged, which is the whole
 * reason they can be applied at all. Each source answers them about the email
 * rather than about the record it is walking, so a matching email is matched
 * in every ledger holding a record of it: the merge downstream sees whole rows
 * and each one reports the status it would have reported unfiltered. Anything
 * narrowed per-source would arrive here half-filtered, and the merge would
 * report a draft as waiting in a mailbox when the send it belongs to had
 * already failed.
 *
 * Retired drafts are always asked for, whatever the reader has chosen. The
 * artifact source puts a row on screen for every email Cara composed, so
 * leaving the retired draft out would not hide that row — it would strip the
 * row of the record explaining where its draft went, and the email would read
 * as never having reached a mailbox at all. Which of those rows to show is
 * decided on the rows themselves, by `outboxRowIsClosed`.
 *
 * The send log alone can come back undefined, because it is the only source
 * with nothing to say by default: an absent filter is what its resolver
 * documents as the unnarrowed read.
 */
export function outboxSourceFilters(opts: { outbox?: OutboxFilterInput }): {
  artifacts: CaraWorkspaceArtifactsFilterInput;
  drafts: MailboxDraftsFilterInput;
  outbound?: OutboundEmailsFilterInput;
} {
  const outbox = opts.outbox;
  return {
    // The artifact library holds every kind of document Cara writes; only one
    // of them is an email, and the rest are no business of an outbox.
    artifacts: { types: [CaraWorkspaceArtifactType.Email], ...(outbox ? { outbox } : {}) },
    drafts: { includeRetired: true, ...(outbox ? { outbox } : {}) },
    outbound: outbox ? { outbox } : undefined
  };
}
